/**
 * PosterComposite — a designed HTML poster in the "EXPLOSION" style
 * used when a site has no static poster image but wants the same visual treatment.
 *
 * Rendered as a portrait 2:3 card with:
 *  - Full-bleed background image (dark tint overlay)
 *  - Top title block (white / gold typography)
 *  - Bottom white block: festival name, edition, location, homage, dates
 *  - Partner logos strip
 */
export default function PosterComposite({
  bgImage,
  eyebrow = "EXPOSITION",
  artist,
  artistCount,
  festival = "LE FIL DU MIDI",
  edition = "CANAL'ART 2026",
  location,
  homage,
  dates,
  logos = [],
}) {
  return (
    <div
      className="relative w-full aspect-[2/3] max-w-[420px] mx-auto overflow-hidden shadow-2xl select-none"
      style={{ background: "#132725" }}
      data-testid="poster-composite"
    >
      {/* Background image + dark tint */}
      {bgImage && (
        <>
          <img
            src={bgImage}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(19,39,37,0.85) 0%, rgba(19,39,37,0.55) 30%, rgba(19,39,37,0.55) 55%, rgba(19,39,37,0.9) 78%, rgba(19,39,37,0.98) 100%)",
            }}
          />
        </>
      )}

      {/* Top title block */}
      <div className="absolute inset-x-0 top-0 px-6 pt-8 md:pt-10 text-center">
        <h1
          className="font-serif font-medium leading-[0.9] tracking-tight text-white uppercase"
          style={{ fontSize: "clamp(28px, 8vw, 46px)" }}
        >
          {eyebrow}
        </h1>
        {artist && (
          <div
            className="mt-2 font-serif italic leading-[0.95] tracking-tight"
            style={{ fontSize: "clamp(30px, 9vw, 52px)", color: "#B98F3E" }}
          >
            {artist}
          </div>
        )}
        {artistCount && (
          <div className="mt-1 font-serif italic text-white/95" style={{ fontSize: "clamp(15px, 3.4vw, 20px)" }}>
            {artistCount}
          </div>
        )}
      </div>

      {/* Bottom white block */}
      <div className="absolute inset-x-0 bottom-0 px-6 pb-6 pt-4 text-center bg-[#F4EFE4] text-[#132725]">
        <div className="font-serif font-medium tracking-tight leading-none" style={{ fontSize: "clamp(19px, 5.2vw, 28px)" }}>
          {festival}
        </div>
        {edition && (
          <div className="mt-1 font-mono text-[10px] md:text-[11px] tracking-[0.24em] uppercase" style={{ color: "#9C6B3E" }}>
            {edition}
          </div>
        )}
        {location && (
          <div className="mt-2 font-serif font-medium tracking-tight leading-tight" style={{ fontSize: "clamp(20px, 5.6vw, 30px)", color: "#132725" }}>
            {location}
          </div>
        )}
        {homage && (
          <div className="mt-1 font-mono text-[9px] md:text-[10px] tracking-[0.2em] uppercase text-[#132725]/70">
            {homage}
          </div>
        )}
        {dates && (
          <div className="mt-3 font-serif tracking-tight leading-none" style={{ fontSize: "clamp(15px, 3.8vw, 22px)", color: "#132725" }}>
            {dates}
          </div>
        )}
        {logos.length > 0 && (
          <div className="mt-4 pt-3 border-t border-[#132725]/15 flex items-center justify-center gap-3 md:gap-4 flex-wrap">
            {logos.map((l, i) => (
              <img
                key={i}
                src={l.url}
                alt={l.alt || ""}
                className="h-6 md:h-7 w-auto object-contain opacity-90"
                loading="lazy"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
